import * as React from 'react';
import { TextField } from '@material-ui/core';
import { RouteLink } from 'src/types/RouteLink';
import styles, { searchMediaStyles } from './TopNavbar.styles';
import { Navigate } from 'react-router-dom';
import { join, isString } from 'lodash';
import { Autocomplete } from '@material-ui/lab';

export interface TopNavSearchProps {
  routeLinks: RouteLink[];
  onEnter: () => void;
  onLeave: () => void;
}
export interface TopNavSearchState {
  minLength: number;
  selectHintOnEnter: boolean;
  newPath: string;
  selectedOption: RouteLink | null;
}

const MINIMUM_CHARACTERS_REQUIRED = 0;

export const getSearchableRoutes = (routeLinks: RouteLink[]) =>
  routeLinks.filter((route) => !route.disabled && !route.hidden);

export class UnmemoizedTopNavSearch extends React.Component<TopNavSearchProps, TopNavSearchState> {
  constructor(props: TopNavSearchProps) {
    super(props);
    this.state = {
      minLength: MINIMUM_CHARACTERS_REQUIRED,
      selectHintOnEnter: true,
      newPath: '',
      selectedOption: null,
    };
  }

  handleInputChange = (_event: React.FormEvent<HTMLFormElement>) => {
    const { selectedOption } = this.state;
    if (selectedOption && selectedOption.link) {
      this.triggerNavigation(selectedOption.link);
    }
  };

  triggerNavigation = (path: string) => {
    if (path) {
      this.setState({
        newPath: path,
      });
      this.props.onLeave();
    }
  };

  renderMenuItemChildren = (option: RouteLink) => {
    if (isString(option)) return <React.Fragment />;
    const breadcrumb = option.highlightPrefix.split('/').slice(2);
    return (
      <React.Fragment>
        {option.name}
        <div key="breadcrumb">
          <small>{breadcrumb.length > 1 && join(breadcrumb, ' > ')}</small>
        </div>
      </React.Fragment>
    );
  };

  selectTypeahead = (_event: React.ChangeEvent<{}>, selected: RouteLink | null) => {
    if (!isString(selected)) {
      this.setState(
        {
          selectedOption: selected,
        },
        () => {
          const newSelectedOption = selected || this.state.selectedOption;
          if (newSelectedOption) {
            this.triggerNavigation(newSelectedOption.link);
          }
        },
      );
    }
  };

  render() {
    const { newPath } = this.state;
    const { routeLinks } = this.props;
    const realRoutes = getSearchableRoutes(routeLinks);
    if (newPath) {
      this.setState({
        newPath: '',
        selectedOption: null,
      });
      return <Navigate to={newPath} />;
    }

    const addonClass = `navbar-brand ${styles.TopNavSearchIconContainer}`;
    return (
      <form onSubmit={this.handleInputChange} data-qa="TopNavSearch" className={searchMediaStyles}>
        <div className={addonClass}>
          <i className="fas fa-search" data-qa="TopNavSearchIcon" />
        </div>
        <div className={styles.TopNavSearch} data-qa="TopNavSearchInput">
          <Autocomplete
            options={realRoutes}
            disableClearable
            onOpen={this.props.onEnter}
            onClose={this.props.onLeave}
            getOptionLabel={(option) => option.name}
            classes={{
              paper: styles.searchPaper,
              inputRoot: styles.searchInput,
              option: styles.optionList,
              listbox: styles.optionListContainer,
              popperDisablePortal: styles.popperStyle,
              endAdornment: styles.endAdornment,
            }}
            renderOption={(props) => this.renderMenuItemChildren(props)}
            onChange={this.selectTypeahead}
            renderInput={(params) => (
              <TextField
                {...params}
                classes={{ root: styles.textFieldStyle }}
                placeholder="Search actions, views and menu items..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  style: { padding: '5px 10px', fontSize: '14px' },
                }}
              />
            )}
            disablePortal
          />
        </div>
      </form>
    );
  }
}

export default React.memo(UnmemoizedTopNavSearch);
